// Project Detail — full v2 parity:
// • Header with KPI breakdown
// • Editable Phases per-program (drag/resize/rename via the same component
//   used in the global Gantt view — single source of truth)
// • Inline milestone editing (title/amount/due/status/note)
// • Project notes timeline (add/delete)
// • Close-out checklist + financials + archive/reopen action
// • Team + scope read-out
import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ExternalLink, CheckCircle, Circle, Plus, Trash2,
  MessageSquare, Archive, RotateCcw, Save, Pencil,
} from "lucide-react";
import { apiClient } from "../../App";
import { fetchJson, fmtMoney, fmtDate, healthBadge, initialsOf, SectionHeader } from "./pccHelpers";

export default function PccProjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [phases, setPhases] = useState([]);
  const [notes, setNotes] = useState([]);
  const [closeOut, setCloseOut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [det, ph, ns, co] = await Promise.all([
        fetchJson(`/planning-command-center/projects/${id}`),
        fetchJson(`/pcc/programs/${id}/phases`).catch(() => []),
        fetchJson(`/pcc/programs/${id}/notes`).catch(() => []),
        fetchJson(`/pcc/programs/${id}/close-out`).catch(() => null),
      ]);
      setData(det); setPhases(ph); setNotes(ns); setCloseOut(co);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, [id]);

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;
  if (!data) return null;

  const p = data.program;
  const hb = healthBadge(p?.risk_level);
  const value = Number(p?.contract_value || 0);
  const collected = Number(p?.cash_collected || 0);
  const outstanding = Math.max(0, value - collected);
  const isArchived = ["Closed", "Archived", "Delivered"].includes(p?.stage);

  return (
    <div className="space-y-5" data-testid="pcc-project-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm" className="text-white/60"><Link to="/planning-command-center"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link></Button>
        <div className="ml-auto flex items-center gap-2">
          {isArchived ? (
            <Button onClick={async () => { try { await apiClient.post(`/pcc/programs/${p.id}/reopen`); toast.success("Reopened"); loadAll(); } catch { toast.error("Failed"); } }} variant="outline" size="sm" className="border-white/15 bg-white/[0.04]" data-testid="pcc-reopen-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen</Button>
          ) : (
            <Button onClick={async () => { if (!window.confirm("Archive this program?")) return; try { await apiClient.post(`/pcc/programs/${p.id}/archive`); toast.success("Archived"); loadAll(); } catch { toast.error("Failed"); } }} variant="outline" size="sm" className="border-white/15 bg-white/[0.04]" data-testid="pcc-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>
          )}
          <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]">
            <a href={`/programs/${p?.id}`} target="_blank" rel="noreferrer">Open in Program <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></a>
          </Button>
        </div>
      </div>

      {/* Header */}
      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <div className="px-6 py-5 border-b border-white/[0.08]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{data.program_type || "Project"}</div>
              <h1 className="text-2xl font-light text-white mt-1">{p.program_name}</h1>
              <p className="text-white/55 text-sm mt-1">{p.client_name}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`border ${hb.cls}`}>{hb.label}</Badge>
              <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/30">{p.stage}</Badge>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 divide-x divide-white/[0.06]">
          <div className="p-4"><div className="text-[10px] uppercase tracking-wider text-white/40">Contract</div><div className="text-xl font-light text-white tabular-nums mt-1">{fmtMoney(value)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-wider text-white/40">Collected</div><div className="text-xl font-light text-emerald-300 tabular-nums mt-1">{fmtMoney(collected)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-wider text-white/40">Outstanding</div><div className="text-xl font-light text-amber-300 tabular-nums mt-1">{fmtMoney(outstanding)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-wider text-white/40">Start</div><div className="text-base font-mono text-white mt-1">{fmtDate(p.start_window)}</div></div>
          <div className="p-4"><div className="text-[10px] uppercase tracking-wider text-white/40">End</div><div className="text-base font-mono text-white mt-1">{fmtDate(p.end_window)}</div></div>
        </div>
      </Card>

      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 360px" }}>
        <div className="space-y-5">
          <PhasesEditor programId={id} phases={phases} onChange={setPhases} />
          <MilestonesEditor payments={data.payments} onRefresh={loadAll} />
          <NotesPanel programId={id} notes={notes} onChange={setNotes} />
        </div>

        <div className="space-y-5">
          <TeamPanel pm={data.pm} team={data.team} />
          {data.quote_summary?.sections?.length > 0 && (
            <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
              <SectionHeader dot="#8FA79B" title="Scope (from Quote)" />
              <div className="p-4">
                <div className="divide-y divide-white/[0.06]">
                  {data.quote_summary.sections.map((s, i) => (
                    <div key={i} className="py-2 flex items-center justify-between gap-2">
                      <span className="text-[12px] text-white/70 truncate">{s.label || s.section_type}</span>
                      <span className="text-[12px] tabular-nums font-mono">{fmtMoney(s.subtotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-white/[0.08] flex items-center justify-between">
                  <span className="text-[12px] uppercase tracking-wider text-white/40">Total</span>
                  <span className="text-sm font-semibold tabular-nums">{fmtMoney(data.quote_summary.subtotal)}</span>
                </div>
              </div>
            </Card>
          )}
          <CloseOutPanel programId={id} closeOut={closeOut} onChange={setCloseOut} />
        </div>
      </div>
    </div>
  );
}

// ── Phases editor (linear list with quick add/rename/delete; the actual
// drag/resize lives in the global Gantt view) ─────────────────────────
function PhasesEditor({ programId, phases, onChange }) {
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");

  async function add() {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const res = await apiClient.post(`/pcc/programs/${programId}/phases`, {
        program_id: programId, name: "New Phase", start_date: today, duration_days: 7, color: "#8FA79B", sort_order: phases.length,
      });
      onChange([...phases, res.data]);
    } catch { toast.error("Failed"); }
  }
  async function patch(p, body) {
    try {
      const res = await apiClient.patch(`/pcc/phases/${p.id}`, body);
      onChange(phases.map(x => x.id === p.id ? res.data : x));
    } catch { toast.error("Failed"); }
  }
  async function del(p) {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await apiClient.delete(`/pcc/phases/${p.id}`); onChange(phases.filter(x => x.id !== p.id)); }
    catch { toast.error("Failed"); }
  }

  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-phases-editor">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <div className="flex items-center gap-2.5">
          <span className="inline-block w-2 h-2 rounded-full bg-purple-400" />
          <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Production Phases ({phases.length})</div>
        </div>
        <Button onClick={add} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black h-7"><Plus className="w-3.5 h-3.5 mr-1" />Phase</Button>
      </div>
      <div className="divide-y divide-white/[0.06]">
        {phases.length === 0 ? (
          <div className="px-6 py-10 text-center text-white/40 text-sm">No phases defined. Add the first one — drag/resize on the Gantt page.</div>
        ) : phases.map(p => (
          <div key={p.id} className="px-6 py-2.5 flex items-center gap-3">
            <div className="w-3 h-3 rounded shrink-0" style={{ background: p.color || "#8FA79B" }} />
            <button onClick={() => patch(p, { is_complete: !p.is_complete })} title="Toggle done" className="shrink-0">
              {p.is_complete ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4 text-white/30" />}
            </button>
            <div className="flex-1 min-w-0">
              {editingId === p.id ? (
                <Input
                  autoFocus value={name} onChange={(e) => setName(e.target.value)}
                  onBlur={() => { if (name && name !== p.name) patch(p, { name }); setEditingId(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingId(null); }}
                  className="bg-black/30 border-white/10 text-white h-7 text-sm"
                />
              ) : (
                <button onClick={() => { setEditingId(p.id); setName(p.name); }} className="text-sm text-white text-left hover:underline">{p.name}</button>
              )}
              <div className="text-[11px] text-white/45 font-mono">{fmtDate(p.start_date)} · {p.duration_days}d</div>
            </div>
            <Input type="date" value={p.start_date?.slice(0, 10) || ""} onChange={(e) => patch(p, { start_date: e.target.value })} className="bg-black/30 border-white/10 text-white h-7 w-32 text-xs" />
            <Input type="number" min="1" value={p.duration_days} onChange={(e) => patch(p, { duration_days: Number(e.target.value) })} className="bg-black/30 border-white/10 text-white h-7 w-20 text-xs" />
            <Button variant="ghost" size="icon" onClick={() => del(p)} className="h-7 w-7"><Trash2 className="w-3.5 h-3.5 text-red-400/70" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Milestones inline editor ───────────────────────────────────────────
function MilestonesEditor({ payments, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});

  async function saveEdit(m) {
    try {
      await apiClient.patch(`/pcc/payment-milestones/${m.id}`, form);
      toast.success("Saved");
      setEditingId(null); setForm({});
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function cycleStatus(m) {
    try { await apiClient.post(`/pcc/payment-milestones/${m.id}/cycle-status`); onRefresh(); }
    catch { toast.error("Failed"); }
  }
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-milestones-editor">
      <SectionHeader dot="#8FA79B" title={`Payment Milestones (${payments?.length || 0})`} />
      <div className="divide-y divide-white/[0.06]">
        {(payments || []).length === 0 ? (
          <div className="px-6 py-10 text-center text-white/40 text-sm">No payment milestones yet.</div>
        ) : (payments || []).map(m => {
          const editing = editingId === m.id;
          const status = (m.status || "pending").toLowerCase();
          const overdue = m.due_date && new Date(m.due_date) < new Date() && status !== "paid";
          return (
            <div key={m.id} className="px-6 py-2.5 flex items-center gap-3">
              <button onClick={() => cycleStatus(m)} title="Cycle status">
                {status === "paid" ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Circle className={`w-4 h-4 ${overdue ? "text-red-400" : status === "invoiced" ? "text-blue-400" : "text-white/30"}`} />}
              </button>
              {editing ? (
                <>
                  <Input value={form.title ?? m.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black/30 border-white/10 text-white h-7 flex-1" />
                  <Input type="number" value={form.calculated_amount ?? m.calculated_amount ?? 0} onChange={(e) => setForm({ ...form, calculated_amount: Number(e.target.value) })} className="bg-black/30 border-white/10 text-white h-7 w-28" />
                  <Input type="date" value={(form.due_date ?? m.due_date)?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-black/30 border-white/10 text-white h-7 w-32 text-xs" />
                  <Button onClick={() => saveEdit(m)} size="icon" className="h-7 w-7 bg-emerald-500 hover:bg-emerald-600 text-black"><Save className="w-3.5 h-3.5" /></Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{m.title}</div>
                    <div className="text-[11px] text-white/45 font-mono">{fmtDate(m.due_date)} · {status}</div>
                  </div>
                  <div className={`text-sm tabular-nums font-mono ${overdue ? "text-red-300" : status === "paid" ? "text-emerald-300" : "text-white"}`}>{fmtMoney(m.calculated_amount || m.paid_amount)}</div>
                  <Button variant="ghost" size="icon" onClick={() => { setEditingId(m.id); setForm({}); }} className="h-7 w-7"><Pencil className="w-3.5 h-3.5 text-white/55" /></Button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Notes timeline ─────────────────────────────────────────────────────
function NotesPanel({ programId, notes, onChange }) {
  const [text, setText] = useState("");
  async function add() {
    const t = text.trim(); if (!t) return;
    try { const res = await apiClient.post(`/pcc/programs/${programId}/notes`, { text: t }); onChange([res.data, ...notes]); setText(""); }
    catch { toast.error("Failed"); }
  }
  async function del(n) {
    try { await apiClient.delete(`/pcc/notes/${n.id}`); onChange(notes.filter(x => x.id !== n.id)); }
    catch { toast.error("Failed"); }
  }
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-notes-panel">
      <SectionHeader dot="#B96A43" title={`Notes (${notes.length})`} />
      <div className="p-4">
        <div className="flex gap-2 mb-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a note…" className="bg-black/30 border-white/10 text-white" data-testid="pcc-note-input" />
          <Button onClick={add} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="pcc-note-add"><MessageSquare className="w-3.5 h-3.5 mr-1" />Add</Button>
        </div>
        <div className="divide-y divide-white/[0.06]">
          {notes.length === 0 ? (
            <div className="py-6 text-center text-white/40 text-sm">No notes yet.</div>
          ) : notes.map(n => (
            <div key={n.id} className="py-2.5 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white whitespace-pre-wrap">{n.text}</div>
                <div className="text-[11px] text-white/45 font-mono mt-0.5">{n.author_name || "system"} · {fmtDate(n.created_at)}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(n)} className="h-6 w-6"><Trash2 className="w-3.5 h-3.5 text-red-400/70" /></Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── Team panel ─────────────────────────────────────────────────────────
function TeamPanel({ pm, team }) {
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
      <SectionHeader dot="#B96A43" title="Team" />
      <div className="p-4 space-y-2">
        {pm && (
          <div className="flex items-center gap-2.5 p-2 bg-amber-500/[0.06] border border-amber-500/20 rounded">
            <div className="w-7 h-7 rounded-full bg-amber-500/25 flex items-center justify-center text-[11px] font-bold text-amber-300">{initialsOf(pm.name)}</div>
            <div className="min-w-0"><div className="text-sm text-white">{pm.name}</div><div className="text-[11px] text-amber-300 font-mono">Program Manager</div></div>
          </div>
        )}
        {(team || []).map(m => (
          <div key={m.user_id} className="flex items-center gap-2.5 p-2 bg-white/[0.02] border border-white/[0.06] rounded">
            <div className="w-7 h-7 rounded-full bg-blue-500/25 flex items-center justify-center text-[11px] font-bold text-blue-300">{initialsOf(m.name)}</div>
            <div className="min-w-0"><div className="text-sm text-white truncate">{m.name}</div><div className="text-[11px] text-white/45 font-mono">{m.role_on_program || m.role}</div></div>
          </div>
        ))}
        {!pm && (team || []).length === 0 && <div className="text-white/40 text-sm">No team assigned.</div>}
      </div>
    </Card>
  );
}

// ── Close-out panel ────────────────────────────────────────────────────
function CloseOutPanel({ programId, closeOut, onChange }) {
  const co = closeOut || {};
  async function patch(body) {
    try {
      const res = await apiClient.put(`/pcc/programs/${programId}/close-out`, { ...co, ...body });
      onChange({ ...co, ...body });
    } catch { toast.error("Failed"); }
  }
  const items = [
    { k: "deliverables_done", label: "All deliverables shipped" },
    { k: "payments_collected", label: "All payments collected" },
    { k: "suppliers_paid", label: "All suppliers paid" },
    { k: "files_archived", label: "Files archived" },
  ];
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-closeout-panel">
      <SectionHeader dot="#8A8783" title="Close-Out Checklist" />
      <div className="p-4 space-y-2">
        {items.map(it => (
          <label key={it.k} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!co[it.k]} onChange={(e) => patch({ [it.k]: e.target.checked })} className="accent-amber-400" />
            <span className="text-sm text-white">{it.label}</span>
          </label>
        ))}
        <div className="pt-2 border-t border-white/[0.06]">
          <label className="text-[10px] uppercase tracking-wider text-white/40 block mb-1">Notes</label>
          <Textarea value={co.close_notes || ""} onChange={(e) => patch({ close_notes: e.target.value })} rows={2} className="bg-black/30 border-white/10 text-white text-sm" placeholder="Wrap-up notes…" />
        </div>
      </div>
    </Card>
  );
}
